import { ReactQuery } from 'cms-api'
import { type PromoCodeIncludedBrandsItem } from 'cms-api/src/axios/cms'
import React, { useEffect, useRef } from 'react'
import { useInfiniteHits } from 'react-instantsearch'
import { Grid } from 'components/Layout'
import { ProductThumbnailCard } from 'components/ProductThumbnail'
import { useUserAuth } from 'providers/userAuth'
import { SPACE } from 'Theme'
import { getProductRoute } from 'utils/getProductRoute'
import { getProductSlug } from 'utils/getProductSlug'

export type ProductHit = {
  id: string
  title: string
  imageUrl: string
  imageAlt: string
  minPrice: number
  maxPrice: number
  minTradePrice: number
  maxTradePrice: number
  minTradeDiscount: number
  maxTradeDiscount: number
  legacyProductId: number
  breakoutOptionCount: number
  carouselVariants: {
    legacyVariantId: number
    imageUrl: string
    imageAlt: string
  }[]
  legacyProductVariantId: number
  brand: string
  availabilityOrder: number
}

export const ProductHits = (props) => {
  const { isLastPage, showMore, hits } = useInfiniteHits<ProductHit>(props)
  const sentinelRef = useRef(null)
  const { data: blackFridayPromoCode } =
    ReactQuery.useGetPromoCodesBlackFriday()
  const { user, isPageLoading } = useUserAuth()

  const blackFridayBrands = (
    blackFridayPromoCode?.data?.includedBrands ?? []
  ).map(({ name }: PromoCodeIncludedBrandsItem & { name: string }) => name)

  useEffect(() => {
    let observer: IntersectionObserver | undefined
    if (sentinelRef.current !== null) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLastPage) {
            showMore?.()
          }
        })
      })

      observer?.observe(sentinelRef.current)
    }

    return () => {
      observer?.disconnect()
    }
  }, [isLastPage, showMore])

  return (
    <Grid
      gridTemplateColumns={{
        MOBILE: '1fr 1fr',
        TABLET: 'repeat(2, 1fr)',
        DESKTOP: 'repeat(3, 1fr)',
      }}
      gap={{
        MOBILE: SPACE.PX_16,
        TABLET: SPACE.PX_32,
      }}
    >
      {hits.map((hit) => {
        const shouldDisplayBlackFridayBadge =
          !isPageLoading &&
          !user?.isTradeCapable &&
          hit.availabilityOrder !== 1 &&
          hit.brand &&
          blackFridayBrands.includes(hit.brand)

        return (
          <ProductThumbnailCard
            key={hit.id}
            title={hit.title}
            productImageAlt={hit.imageAlt}
            productImageUrl={hit.imageUrl}
            price={hit.minPrice} // Temporary workaround
            tradePrice={hit.minTradePrice}
            priceRange={{ min: hit.minPrice, max: hit.maxPrice }}
            tradePriceRange={{ min: hit.minTradePrice, max: hit.maxTradePrice }}
            tradeDiscount={hit.minTradeDiscount}
            url={getProductRoute({
              slug: getProductSlug({
                id: hit.legacyProductId,
                title: hit.title,
              }),
            })}
            breakoutOptionCount={hit.breakoutOptionCount}
            carouselImages={hit.carouselVariants}
            onRefetch={() => {}}
            variantId={hit.legacyProductVariantId}
            productId={hit.legacyProductId}
            availabilityOrder={hit.availabilityOrder}
            shouldDisplayBlackFridayBadge={shouldDisplayBlackFridayBadge}
            brand={hit.brand}
          />
        )
      })}
      {!isLastPage && <div ref={sentinelRef} aria-hidden="true" />}
    </Grid>
  )
}
